/**
 * Central error-message mapping: technical error codes/patterns → user-friendly messages.
 * All user-facing error text flows through getUserMessage() to ensure
 * no internal details leak to end users.
 */

const ERROR_MAP: Record<string, string> = {
  NETWORK_ERROR:
    "We're having trouble connecting. Please check your internet connection and try again.",
  UNAUTHORIZED:
    "Your session has expired. Please log in again to continue.",
  FORBIDDEN:
    "You don't have permission to perform this action. Please contact your law firm.",
  NOT_FOUND:
    "The requested information could not be found. Please try again or contact your law firm.",
  VERIFICATION_FAILED:
    "We were unable to complete your verification at this time. Please contact your law firm or try again later.",
  DOCUSIGN_ERROR:
    "We encountered an issue preparing your documents. Please try again shortly.",
  PAYMENT_ERROR:
    "There was a problem processing your payment setup. Please try again or contact your law firm.",
  UPLOAD_ERROR:
    "We couldn't upload your document. Please check the file and try again.",
  VALIDATION_ERROR:
    "Some of the information provided needs correction. Please review and try again.",
  SESSION_EXPIRED:
    "Your session has expired. Please log in again.",
  RATE_LIMITED:
    "Too many requests. Please wait a moment and try again.",
  SERVER_ERROR:
    "Something went wrong on our end. Please try again later or contact your law firm for assistance.",
};

const DEFAULT_MESSAGE =
  "We were unable to complete your request. Please try again or contact your law firm for assistance.";

export function getUserMessage(error: Error | string): string {
  const raw = typeof error === "string" ? error : error.message;

  if (raw.includes("401") || raw.toLowerCase().includes("unauthorized")) {
    return ERROR_MAP.UNAUTHORIZED;
  }
  if (raw.includes("403") || raw.toLowerCase().includes("forbidden")) {
    return ERROR_MAP.FORBIDDEN;
  }
  if (raw.includes("404") || raw.toLowerCase().includes("not found")) {
    return ERROR_MAP.NOT_FOUND;
  }
  if (raw.includes("429") || raw.toLowerCase().includes("rate limit")) {
    return ERROR_MAP.RATE_LIMITED;
  }
  if (
    raw.toLowerCase().includes("network") ||
    raw.toLowerCase().includes("fetch")
  ) {
    return ERROR_MAP.NETWORK_ERROR;
  }
  if (raw.toLowerCase().includes("verification")) {
    return ERROR_MAP.VERIFICATION_FAILED;
  }
  if (raw.toLowerCase().includes("docusign") || raw.toLowerCase().includes("envelope")) {
    return ERROR_MAP.DOCUSIGN_ERROR;
  }
  if (raw.toLowerCase().includes("payment") || raw.toLowerCase().includes("gocardless")) {
    return ERROR_MAP.PAYMENT_ERROR;
  }
  if (raw.toLowerCase().includes("upload") || raw.toLowerCase().includes("multer")) {
    return ERROR_MAP.UPLOAD_ERROR;
  }
  if (raw.includes("500") || raw.toLowerCase().includes("internal server")) {
    return ERROR_MAP.SERVER_ERROR;
  }

  for (const [, friendly] of Object.entries(ERROR_MAP)) {
    if (raw === friendly) return friendly;
  }

  return DEFAULT_MESSAGE;
}

export function getErrorByCode(code: string): string {
  return ERROR_MAP[code] ?? DEFAULT_MESSAGE;
}
